import { HiMenu, HiChat, HiBell, HiUserCircle, HiChevronDown, HiQuestionMarkCircle, HiAnnotation } from 'react-icons/hi'
import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useNotifications } from '../../hooks/useNotifications'
import NotificationDropdown from '../common/NotificationDropdown'
import FeedbackModal from '../common/FeedbackModal'

export default function TopBar({ onToggleSidebar, onOpenHelp, isAIChatPage }) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [notifOpen, setNotifOpen] = useState(false)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const menuRef = useRef(null)
  const notifRef = useRef(null)
  const {
    notifications,
    unreadCount,
    loading: notifLoading,
    fetchNotifications,
    markRead,
    markAllRead,
  } = useNotifications()

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <header className="relative z-50 h-14 bg-white dark:bg-surface-800 border-b border-surface-200 dark:border-surface-700 flex items-center justify-between px-3 sm:px-4 xl:px-6 shrink-0">
      <div className="flex items-center gap-2 sm:gap-4">
        <button
          onClick={onToggleSidebar}
          className="p-2 text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-700 hover:text-surface-700 dark:hover:text-surface-300 active:bg-surface-200 dark:active:bg-surface-600 rounded-lg transition-all duration-150"
          aria-label="Toggle sidebar"
        >
          <HiMenu className="w-5 h-5" />
        </button>
        
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-brand-500 to-brand-700 rounded-lg flex items-center justify-center shadow-sm">
            <span className="text-white font-bold text-sm">PT</span>
          </div>
          <span className="font-semibold text-surface-800 dark:text-surface-200 hidden sm:block">
            Project Tracker
          </span>
        </div>
      </div>

      <div className="flex items-center gap-1.5">
        {/* AI Chat - Navigate to full chat page */}
        <button
          onClick={() => navigate('/ai-chat')}
          data-tour="ai-chat"
          className={`p-2 rounded-lg transition-all duration-150 flex items-center gap-1.5 ${
            isAIChatPage
              ? 'bg-gradient-to-br from-ai-100 to-purple-100 text-ai-700 shadow-sm dark:from-ai-900/40 dark:to-purple-900/40 dark:text-ai-300 ring-1 ring-ai-200 dark:ring-ai-700'
              : 'text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-700 hover:text-surface-700 dark:hover:text-surface-300 active:bg-surface-200'
          }`}
          aria-label="Open HAL 9000"
          title="HAL 9000"
        >
          <HiChat className="w-5 h-5" />
        </button>

        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={() => setNotifOpen((o) => !o)}
            className="p-2 text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-700 hover:text-surface-700 dark:hover:text-surface-300 active:bg-surface-200 rounded-lg transition-all duration-150 relative"
            aria-label="Notifications"
          >
            <HiBell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 min-w-[16px] h-4 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 ring-2 ring-white dark:ring-surface-800">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
          <NotificationDropdown
            open={notifOpen}
            onClose={() => setNotifOpen(false)}
            notifications={notifications}
            loading={notifLoading}
            onFetch={fetchNotifications}
            onMarkRead={markRead}
            onMarkAllRead={markAllRead}
            unreadCount={unreadCount}
          />
        </div>

        {/* Feedback */}
        <button
          onClick={() => setFeedbackOpen(true)}
          className="p-2 text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-700 hover:text-surface-700 dark:hover:text-surface-300 active:bg-surface-200 rounded-lg transition-all duration-150"
          aria-label="Send feedback"
          title="Send Feedback"
        >
          <HiAnnotation className="w-5 h-5" />
        </button>

        {/* Help Button */}
        <button
          onClick={onOpenHelp}
          className="p-2 text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-700 hover:text-surface-700 dark:hover:text-surface-300 active:bg-surface-200 rounded-lg transition-all duration-150"
          aria-label="Help center"
          title="Help & Getting Started"
        >
          <HiQuestionMarkCircle className="w-5 h-5" />
        </button>

        {/* User Menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center gap-2 p-2 hover:bg-surface-100 dark:hover:bg-surface-700 active:bg-surface-200 rounded-lg transition-all duration-150"
            aria-label="Open user menu"
            aria-expanded={userMenuOpen}
          >
            <div className="w-7 h-7 bg-gradient-to-br from-brand-100 to-brand-200 rounded-full flex items-center justify-center">
              <span className="text-sm font-medium text-brand-700">
                {user?.display_name?.charAt(0) || 'U'}
              </span>
            </div>
            <span className="text-sm font-medium text-surface-700 dark:text-surface-300 hidden sm:block">
              {user?.display_name || 'User'}
            </span>
            <HiChevronDown className={`hidden sm:block w-4 h-4 text-surface-400 transition-transform duration-200 ${
              userMenuOpen ? 'rotate-180' : ''
            }`} />
          </button>

          {userMenuOpen && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl shadow-lg py-2 z-50 animate-fade-in">
              <div className="px-4 py-3 border-b border-surface-100 dark:border-surface-700">
                <p className="text-sm font-semibold text-surface-800 dark:text-surface-200">{user?.display_name}</p>
                <p className="text-xs text-surface-500 dark:text-surface-400 truncate">{user?.email}</p>
              </div>
              <div className="py-1">
                <a
                  href="/settings"
                  className="block px-4 py-2.5 text-sm text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-700 hover:text-surface-900 dark:hover:text-surface-100 transition-colors"
                >
                  Settings
                </a>
              </div>
              <div className="border-t border-surface-100 dark:border-surface-700 pt-1">
                <button
                  onClick={logout}
                  className="block w-full text-left px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Feedback Modal */}
      {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}
    </header>
  )
}
